import { useState, useEffect, useCallback } from 'react';
import { ClockCounterClockwise, MagnifyingGlass, X, CaretLeft, CaretRight } from '@phosphor-icons/react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Badge,
} from '@t-one-internal/design-system';
import { getAuditEvents, type AuditEvent, type GetAuditEventsParams } from '../../api/auditApi';

/** Action display names */
const ACTION_NAMES: Record<string, string> = {
  CREATE: 'Created', UPDATE: 'Updated', DELETE: 'Deleted',
  APPROVE: 'Approved', REJECT: 'Rejected', ADJUST_POINTS: 'Points Adjusted',
};

/** Action badge colors */
const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-green-50 text-green-700 border-green-200',
  DELETE: 'bg-red-50 text-red-700 border-red-200',
  APPROVE: 'bg-green-50 text-green-700 border-green-200',
  REJECT: 'bg-red-50 text-red-700 border-red-200',
  ADJUST_POINTS: 'bg-blue-50 text-blue-700 border-blue-200',
};

/** Hidden internal fields */
const HIDDEN = new Set(['id', 'account_id', 'user_id', 'created_at', 'updated_at', 'entity_type']);

/** Format field name */
const fieldLabel = (f: string) =>
  f.replace(/^is_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Format value */
const fmtVal = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '-';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number' && Math.abs(v) > 9999999999) return '-';
  return String(v);
};

/** Group events by date */
const groupByDate = (events: AuditEvent[]) => {
  const groups: Record<string, AuditEvent[]> = {};
  for (const e of events) {
    const d = new Date(e.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    (groups[d] ??= []).push(e);
  }
  return Object.entries(groups);
};

interface Props {
  entityType: string;
  entityId?: string;
  actions?: string[];
  title?: string;
  tooltip?: string;
}

export const AuditHistoryButton: React.FC<Props> = ({
  entityType,
  entityId,
  actions = ['CREATE', 'DELETE', 'APPROVE', 'REJECT', 'ADJUST_POINTS'],
  title = 'History',
  tooltip = 'View History',
}) => {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const limit = 20;

  const fetchData = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const params: GetAuditEventsParams = {
        entity_type: entityType,
        page,
        limit,
        ...(entityId && { entity_id: entityId }),
        ...(search && { search }),
        ...(selectedActions.length > 0 && { action: selectedActions }),
      };
      const res = await getAuditEvents(params);
      setEvents(res.data ?? []);
      setTotal(res.meta?.total ?? 0);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [open, entityType, entityId, page, search, selectedActions]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (open) { setPage(1); setSearch(''); setSearchInput(''); setSelectedActions([]); } }, [open]);

  const totalPages = Math.ceil(total / limit);

  return (
    <>
      <Button variant="outline" size="icon" onClick={() => setOpen(true)} title={tooltip}
        className="h-8 w-8 text-slate-600 hover:text-blue-600 border-slate-200">
        <ClockCounterClockwise size={16} />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl h-[75vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between shrink-0">
            <DialogTitle className="text-lg font-semibold">{title}</DialogTitle>
          </DialogHeader>

          {/* Filters */}
          <div className="px-6 pt-3 pb-2 shrink-0 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-wrap gap-1 flex-1">
                {actions.map(a => (
                  <button key={a} onClick={() => setSelectedActions(prev =>
                    prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]
                  )}
                    className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase border transition-all ${
                      selectedActions.includes(a)
                        ? (ACTION_COLORS[a] || 'bg-slate-50 text-slate-700 border-slate-200') + ' ring-1 ring-offset-1 ring-current'
                        : 'bg-white text-slate-400 border-slate-200 hover:border-slate-300'
                    }`}>
                    {ACTION_NAMES[a] || a}
                  </button>
                ))}
                {selectedActions.length > 0 && (
                  <button onClick={() => setSelectedActions([])}
                    className="px-2 py-0.5 text-[10px] text-slate-500 underline">Clear</button>
                )}
              </div>
              <form onSubmit={e => { e.preventDefault(); setSearch(searchInput); setPage(1); }}
                className="flex items-center gap-1.5 shrink-0">
                <div className="relative">
                  <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)}
                    placeholder="Search..." className="w-40 pl-7 pr-7 py-1 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-400" />
                  {searchInput && (
                    <button type="button" onClick={() => { setSearchInput(''); setSearch(''); }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      <X size={12} />
                    </button>
                  )}
                </div>
                <Button variant="outline" size="sm" type="submit" className="text-xs h-6 px-2">Search</Button>
              </form>
            </div>
          </div>

          {/* Timeline */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-4">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
              </div>
            ) : events.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-12">No audit events found.</div>
            ) : (
              groupByDate(events).map(([date, items]) => (
                <div key={date} className="mb-4">
                  <div className="sticky top-0 bg-white z-10 py-1.5 text-xs font-semibold text-slate-500 uppercase border-b mb-2">{date}</div>
                  {items.map(ev => {
                    const visibleChanges = (ev.changes || []).filter(c => !HIDDEN.has(c.field));
                    const isExpanded = expandedId === ev.id;
                    return (
                      <div key={ev.id} className="flex gap-3 mb-3">
                        <div className="flex flex-col items-center pt-1">
                          <div className={`w-2.5 h-2.5 rounded-full border-2 ${
                            ev.action === 'CREATE' || ev.action === 'APPROVE' ? 'border-green-500' :
                            ev.action === 'DELETE' || ev.action === 'REJECT' ? 'border-red-500' : 'border-blue-500'
                          }`} />
                          <div className="w-px flex-1 bg-slate-200 mt-1" />
                        </div>
                        <div className="flex-1 pb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge className={`text-[10px] px-1.5 py-0 ${ACTION_COLORS[ev.action] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                              {ACTION_NAMES[ev.action] || ev.action}
                            </Badge>
                            <span className="text-sm">
                              <strong>{ev.actor_name || 'System'}</strong>
                              <span className="text-slate-500"> {(ACTION_NAMES[ev.action] || ev.action).toLowerCase()} </span>
                              <strong>Recognition</strong>
                              {ev.metadata?.entity_name && <span className="text-slate-500"> — {String(ev.metadata.entity_name)}</span>}
                            </span>
                            <span className="ml-auto text-[11px] text-slate-400">
                              {new Date(ev.created_at).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {ev.ip_address && (
                            <div className="text-[11px] text-slate-400 mt-0.5">@ {ev.ip_address}</div>
                          )}
                          {visibleChanges.length > 0 && (
                            <button onClick={() => setExpandedId(isExpanded ? null : ev.id)}
                              className="text-xs text-indigo-600 mt-1 hover:underline">
                              {isExpanded ? 'Hide details' : `View ${visibleChanges.length} change${visibleChanges.length > 1 ? 's' : ''}`} {isExpanded ? '∧' : '∨'}
                            </button>
                          )}
                          {isExpanded && visibleChanges.length > 0 && (
                            <div className="mt-2 rounded border border-slate-200 overflow-hidden">
                              <table className="w-full text-xs">
                                <thead><tr className="bg-slate-50 text-slate-500">
                                  <th className="text-left px-3 py-1.5 font-medium">Field</th>
                                  <th className="text-left px-3 py-1.5 font-medium">Previous</th>
                                  <th className="text-left px-3 py-1.5 font-medium">New Value</th>
                                </tr></thead>
                                <tbody>
                                  {visibleChanges.map((ch, i) => (
                                    <tr key={i} className="border-t border-slate-100">
                                      <td className="px-3 py-1.5 text-slate-600">{fieldLabel(ch.field)}</td>
                                      <td className="px-3 py-1.5 text-red-500 line-through">{fmtVal(ch.from)}</td>
                                      <td className="px-3 py-1.5 text-green-600">{fmtVal(ch.to)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {total > 0 && (
            <div className="shrink-0 border-t px-6 py-2 flex items-center justify-between text-xs text-slate-500">
              <span>{total} event{total !== 1 ? 's' : ''}</span>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="h-6 px-2">
                  <CaretLeft size={14} /> Prev
                </Button>
                <span>{page} / {totalPages || 1}</span>
                <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="h-6 px-2">
                  Next <CaretRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AuditHistoryButton;
